import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, OnDestroy} from '@angular/core';
import {DateRange, MatCalendar, MatCalendarView, MatDateRangePicker} from '@angular/material/datepicker';
import {DateAdapter, MAT_DATE_FORMATS, MatDateFormats} from '@angular/material/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {
  DATEPICKER_PRESET,
  DATEPICKER_PRESET_CURRENT_MONTH,
  DATEPICKER_PRESET_CURRENT_WEEK,
  DATEPICKER_PRESET_CURRENT_YEAR,
  DATEPICKER_PRESET_CUSTOM,
  DATEPICKER_PRESET_LAST_3_DAYS,
  DATEPICKER_PRESET_LAST_MONTH,
  DATEPICKER_PRESET_LAST_WEEK,
  DATEPICKER_PRESET_TODAY,
  DATEPICKER_PRESET_YESTERDAY,
  DATEPICKER_PRESETS
} from '../datepicker.constants';
import moment from 'moment';
import {ViewService} from '../../../services/view/view.service';
import {UtilService} from '../../../services/util/util.service';

@Component({
  selector: 'app-datepicker-custom-range-header',
  styleUrls: ['../datepicker-header.component.scss'],
  templateUrl: './datepicker-customRangeHeader.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerCustomRangeHeaderComponent<D> implements AfterViewInit, OnDestroy {
  private _destroyed: Subject<void> = new Subject<void>();
  readonly customPresets = DATEPICKER_PRESETS.filter(v => this.viewService.dateRanges.includes(v));
  public noPresets: boolean;
  public onlyMonths: boolean;

  @HostBinding('class.touch-ui')
  readonly isTouchUi = this.picker.touchUi;

  readonly _formatShort: string;
  readonly _formatLong: string;

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    private picker: MatDateRangePicker<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    private cdr: ChangeDetectorRef,
    private viewService: ViewService,
    public utilService: UtilService
  ) {
    const {start, end} = this._calendar.selected as DateRange<D>;
    this.noPresets = this.picker.panelClass.includes('calendar-no-preset');
    this.onlyMonths = this.picker.panelClass.includes('calendar-only-months');
    if (!start && !end && !this.noPresets) {
      this.updatePreset(DATEPICKER_PRESET_CUSTOM);
    }
    _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => {
      if (this.onlyMonths && this._calendar.currentView === 'month') {
        this._calendar.currentView = 'year';
      }
      cdr.markForCheck();
      cdr.detectChanges();
    });
    _calendar._userSelection.pipe(takeUntil(this._destroyed)).subscribe(() => {
      const range: DateRange<D> = _calendar.selected as DateRange<D>;
      this.picker.select(range.start);
      this.picker._applyPendingSelection();
      this.picker.select(range.end);
      this.picker._applyPendingSelection();
      if (!this.noPresets) {
        this.picker.panelClass = [];
      }
      cdr.markForCheck();
      cdr.detectChanges();
    });
    if (this.onlyMonths) {
      _calendar.monthSelected.pipe(takeUntil(this._destroyed)).subscribe((selectedMonth: D) => {
        if (this._dateAdapter.getMonth(selectedMonth) === this._dateAdapter.getMonth(this.today)) {
          this.selectRange(DATEPICKER_PRESET_CURRENT_MONTH);
        } else {
          this.applyRange(
            this._dateAdapter.createDate(this._dateAdapter.getYear(selectedMonth), this._dateAdapter.getMonth(selectedMonth), 1),
            this._dateAdapter.createDate(this._dateAdapter.getYear(selectedMonth), this._dateAdapter.getMonth(selectedMonth), this._dateAdapter.getNumDaysInMonth(selectedMonth)),
            this._dateAdapter.getMonth(selectedMonth) === this._dateAdapter.getMonth(this._dateAdapter.addCalendarMonths(this.today, -1)) ? DATEPICKER_PRESET_LAST_MONTH : null
          );
        }
      });
    }
    this._formatShort = viewService.conf.datePickerShort;
    this._formatLong = viewService.conf.datePickerLong;
  }

  private updatePreset(preset: string): void {
    this.picker.panelClass = [];
    if (this.onlyMonths) {
      this.picker.panelClass.push('calendar-only-months');
    }
    this.picker.panelClass.push(preset);
  }

  ngAfterViewInit(): void {
    if (!this.noPresets) {
      this.setDefaultPanelClass();
    }
    this.setCalendars();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }
  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  public hasPreset(preset: string): boolean {
    return this.picker.panelClass.includes(preset);
  }

  get getCalendarRange(): string {
    const {start} = this._calendar.selected as DateRange<D>;
    const {end} = this._calendar.selected as DateRange<D>;
    if (!start && !end) {
      return '';
    }
    const sameYear = moment(start).get('year') === moment(end).get('year');
    return (start ? moment(start).format(sameYear ? this._formatShort : this._formatLong) : '...') +
      (end ? ' - ' + moment(end).format(this._formatLong) : ' - ...');
  }

  periodMonthLabel(): string {
    return moment(this._calendar.activeDate).format('MMMM');
  }
  periodYearLabel(): string {
    return moment(this._calendar.activeDate).format('YYYY');
  }

  previousClicked(): void {
    this.setCalendars(-1);
  }
  nextClicked(): void {
    this.setCalendars(1);
  }

  private calculateDateRange(rangeName: DATEPICKER_PRESET): [start: D, end: D, preset: DATEPICKER_PRESET] {
    const today: D = this.today;
    const month: number = this._dateAdapter.getMonth(today);
    const year: number = this._dateAdapter.getYear(today);
    switch (rangeName) {
      case DATEPICKER_PRESET_TODAY: {
        return [today, today, rangeName];
      }
      case DATEPICKER_PRESET_YESTERDAY: {
        const yesterday: D = this._dateAdapter.addCalendarDays(today, -1);
        return [yesterday, yesterday, rangeName];
      }
      case DATEPICKER_PRESET_LAST_WEEK: {
        const start: D = this._dateAdapter.addCalendarDays(today, this._dateAdapter.getDayOfWeek(today) * -1 + 1 - 7);
        const end: D = this._dateAdapter.addCalendarDays(today, this._dateAdapter.getDayOfWeek(today) * -1);
        return [start, end, rangeName];
      }
      case DATEPICKER_PRESET_CURRENT_WEEK: {
        const start: D = this._dateAdapter.addCalendarDays(today, this._dateAdapter.getDayOfWeek(today) * -1 + 1);
        return [start, today, rangeName];
      }
      case DATEPICKER_PRESET_CURRENT_MONTH: {
        const start: D = this._dateAdapter.createDate(year, month, 1);
        return [start, today, rangeName];
      }
      case DATEPICKER_PRESET_CURRENT_YEAR: {
        const start: D = this._dateAdapter.createDate(year, 0, 1);
        this.updatePreset(DATEPICKER_PRESET_CURRENT_YEAR);
        return [start, today, rangeName];
      }
      case DATEPICKER_PRESET_LAST_3_DAYS: {
        const start: D = this._dateAdapter.addCalendarDays(today, -2);
        return [start, today, rangeName];
      }
      case DATEPICKER_PRESET_LAST_MONTH: {
        const start: D = this._dateAdapter.addCalendarMonths(this._dateAdapter.createDate(year, month, 1), -1);
        const end: D = this._dateAdapter.createDate(this._dateAdapter.getYear(start), this._dateAdapter.getMonth(start), this._dateAdapter.getNumDaysInMonth(start));
        return [start, end, rangeName];
      }
      default:
        return [today, today, DATEPICKER_PRESET_TODAY];
    }
  }

  private get today(): D {
    const now = this._dateAdapter.today();
    return this._dateAdapter.createDate(this._dateAdapter.getYear(now), this._dateAdapter.getMonth(now), this._dateAdapter.getDate(now));
  }

  selectRange(rangeName: DATEPICKER_PRESET): void {
    const [start, end, preset] = this.calculateDateRange(rangeName);
    this.applyRange(start, end, preset);
    /* temporary: applying one more time to avoid previous user selection */
    this.applyRange(start, end, preset);
  }

  applyRange(start: D, end: D, preset: DATEPICKER_PRESET): void {
    this._calendar.selected = new DateRange(start, end);
    this._calendar.activeDate = start;
    this.setCalendars();
    this.picker.select(start);
    this._calendar._userSelection.emit({value: start, event: null});
    this.picker._applyPendingSelection();
    this.picker.select(end);
    this._calendar._userSelection.emit({value: end, event: null});
    this.picker._applyPendingSelection();
    if (!this.noPresets) {
      this.updatePreset(preset);
    }
  }

  switchCalendarView(view: MatCalendarView): void {
    if (this._calendar.currentView !== view) {
      this._calendar.currentView = view;
    } else {
      this._calendar.currentView = this.onlyMonths ? 'year' : 'month';
    }
  }

  setCalendars(inMonths: number = 0): void {
    this._calendar.activeDate = this._dateAdapter.addCalendarMonths(this._calendar.activeDate, inMonths);
    this._calendar.currentView = this.onlyMonths ? 'year' : 'month';
  }

  setDefaultPanelClass(): void {
    const range: DateRange<D> = this._calendar.selected as DateRange<D>;
    const today: D = this.today;
    const month: number = this._dateAdapter.getMonth(this.today);
    const year: number = this._dateAdapter.getYear(this.today);
    if (moment(range.start).startOf('day').isSame(moment(this._dateAdapter.addCalendarDays(today, this._dateAdapter.getDayOfWeek(today) * -1 + 1 - 7)).startOf('day')) && moment(range.end)
      .startOf('day').isSame(moment(this._dateAdapter.addCalendarDays(today, this._dateAdapter.getDayOfWeek(today) * -1)).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_LAST_WEEK);
    }
    if (moment(range.start).isSame(range.end) && moment(range.start).startOf('day').isSame(moment(today).add(-1, 'days').startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_YESTERDAY);
    }
    if (moment(range.start).isSame(range.end) && moment(range.start).startOf('day').isSame(moment(today).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_TODAY);
    }
    if (moment(range.start).startOf('day').isSame(moment(this._dateAdapter.addCalendarDays(today, this._dateAdapter.getDayOfWeek(today) * -1 + 1)).startOf('day')) && moment(range.end).startOf('day').isSame(moment(today).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_CURRENT_WEEK);
    }
    if (moment(range.start).startOf('day').isSame(moment(this._dateAdapter.createDate(year, month, 1)).startOf('day')) && moment(range.end).startOf('day').isSame(moment(today).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_CURRENT_MONTH);
    }
    if (moment(range.start).startOf('day').isSame(moment(this._dateAdapter.createDate(year, 0, 1)).startOf('day')) && moment(range.end).startOf('day').isSame(moment(today).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_CURRENT_YEAR);
    }
    if (moment(range.start).startOf('day').isSame(moment(today).add(-2, 'days').startOf('day')) && moment(range.end).startOf('day').isSame(moment(today).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_LAST_3_DAYS);
    }
    const previousMonthDateStart: D = this._dateAdapter.addCalendarMonths(this._dateAdapter.createDate(year, month, 1), -1);
    const previousMonthDateEnd: D = this._dateAdapter.createDate(this._dateAdapter.getYear(previousMonthDateStart), this._dateAdapter.getMonth(previousMonthDateStart), this._dateAdapter.getNumDaysInMonth(previousMonthDateStart));
    if (moment(range.start).startOf('day').isSame(moment(previousMonthDateStart).startOf('day')) &&
      moment(range.end).startOf('day').isSame(moment(previousMonthDateEnd).startOf('day'))) {
      this.updatePreset(DATEPICKER_PRESET_LAST_MONTH);
    }
  }
}
